import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { RequestButton } from './RequestButton';
import {
  Music, Video, Mic, Play, Pause, User, ListMusic, Link2, Search,
  X, Check, Loader2, ImageIcon, Radio
} from 'lucide-react';

const ATTACHMENT_TYPES = {
  song: { label: 'Song', icon: Music, color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  episode: { label: 'Episode', icon: Mic, color: 'text-green-500', bgColor: 'bg-green-500/10' },
  video: { label: 'Video', icon: Video, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  clip: { label: 'Clip', icon: Video, color: 'text-pink-500', bgColor: 'bg-pink-500/10' },
  creator: { label: 'Creator', icon: User, color: 'text-orange-500', bgColor: 'bg-orange-500/10' },
  playlist: { label: 'Playlist', icon: ListMusic, color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
  external: { label: 'External Link', icon: Link2, color: 'text-gray-500', bgColor: 'bg-gray-500/10' },
};

// Default cover placeholders by type
const DEFAULT_COVERS = {
  song: '/placeholder-song.png',
  episode: '/placeholder-podcast.png',
  video: '/placeholder-video.png',
  clip: '/placeholder-video.png',
  creator: '/placeholder-avatar.png',
  playlist: '/placeholder-playlist.png',
  external: '/placeholder-link.png',
};

/**
 * Content Attachment Picker
 * Allows users to attach platform content (songs, videos, creators, etc.) to posts/comments
 */
export function ContentAttachmentPicker({ 
  isOpen, 
  onClose, 
  onSelect,
  allowedTypes = ['song', 'episode', 'video', 'clip', 'creator']
}) {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('song');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch content based on type
  const fetchContent = async (type, query = '') => {
    setLoading(true);
    setItems([]);
    
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      let endpoint = '';
      
      switch (type) {
        case 'song':
          endpoint = query 
            ? `/tracks/search?q=${encodeURIComponent(query)}&limit=20`
            : `/tracks?limit=20`;
          break;
        case 'video':
        case 'clip':
          endpoint = query
            ? `/videos/search?q=${encodeURIComponent(query)}&limit=20`
            : `/videos?limit=20`;
          break;
        case 'creator':
          endpoint = query
            ? `/artists/search?q=${encodeURIComponent(query)}&limit=20`
            : `/artists?limit=20`;
          break;
        case 'episode':
          endpoint = query
            ? `/podcasts/episodes/search?q=${encodeURIComponent(query)}&limit=20`
            : `/podcasts/episodes?limit=20`;
          break;
        case 'playlist':
          endpoint = `/playlists?limit=20`;
          break;
        default:
          return;
      }

      const res = await fetch(`${API}${endpoint}`, { headers });
      if (res.ok) {
        const data = await res.json();
        // Normalize the data structure
        const normalized = normalizeItems(data, type);
        setItems(normalized);
      }
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  // Normalize different API response formats
  const normalizeItems = (data, type) => {
    let items = Array.isArray(data) ? data : data.tracks || data.videos || data.artists || data.episodes || data.playlists || [];
    
    return items.map(item => ({
      id: item.track_id || item.video_id || item.artist_id || item.episode_id || item.playlist_id || item.id,
      type: type,
      title: item.title || item.name || item.stage_name || item.artist_name || 'Untitled',
      subtitle: item.artist_name || item.creator_name || item.handle || '',
      coverUrl: item.cover_art_url || item.cover_url || item.thumbnail_url || item.avatar_url || item.picture || null,
      duration: item.duration || 0,
      ownerId: item.artist_id || item.user_id || item.owner_id,
      ownerName: item.artist_name || item.creator_name || item.owner_name || '',
      playUrl: item.audio_url || item.file_url || item.video_url || item.stream_url || null,
      isPrivate: item.is_private || item.visibility === 'private',
      raw: item // Keep original for reference
    }));
  };

  useEffect(() => {
    if (isOpen && activeTab) {
      fetchContent(activeTab, searchQuery);
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (isOpen && searchQuery) {
        fetchContent(activeTab, searchQuery);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelect = () => {
    if (selectedItem) {
      if (selectedItem.isPrivate) {
        toast.error('This content is private and cannot be attached to public posts.');
        return;
      }
      onSelect({
        attachmentType: selectedItem.type,
        attachmentId: selectedItem.id,
        attachmentOwnerId: selectedItem.ownerId,
        attachmentTitle: selectedItem.title,
        attachmentSubtitle: selectedItem.subtitle,
        attachmentCoverUrl: selectedItem.coverUrl,
        attachmentDuration: selectedItem.duration,
        attachmentPlayUrl: selectedItem.playUrl,
        attachmentOwnerName: selectedItem.ownerName,
      });
      onClose();
    }
  };

  const TypeIcon = ATTACHMENT_TYPES[activeTab]?.icon || Music;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="content-attachment-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TypeIcon className={`w-5 h-5 ${ATTACHMENT_TYPES[activeTab]?.color}`} />
            Attach Content
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-5 w-full">
            {allowedTypes.map(type => {
              const config = ATTACHMENT_TYPES[type];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <TabsTrigger key={type} value={type} className="gap-1 text-xs">
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{config.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Search */}
          <div className="py-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${ATTACHMENT_TYPES[activeTab]?.label || 'content'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="attachment-search"
              />
            </div>
          </div>

          {/* Content List */}
          <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[300px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <TypeIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No {ATTACHMENT_TYPES[activeTab]?.label || 'content'} found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <ContentItem
                    key={item.id}
                    item={item}
                    isSelected={selectedItem?.id === item.id}
                    onSelect={() => setSelectedItem(item)}
                  />
                ))}
              </div>
            )}
          </div>
        </Tabs>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSelect} 
            disabled={!selectedItem}
            data-testid="confirm-attachment-button"
          >
            <Check className="w-4 h-4 mr-2" />
            Attach {selectedItem ? ATTACHMENT_TYPES[selectedItem.type]?.label : 'Content'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Individual content item in the list
function ContentItem({ item, isSelected, onSelect }) {
  const config = ATTACHMENT_TYPES[item.type];
  const Icon = config?.icon || Music;

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      onClick={onSelect}
      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'bg-primary/10 border border-primary' 
          : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
      } ${item.isPrivate ? 'opacity-60' : ''}`}
      data-testid={`content-item-${item.id}`}
    >
      {/* Cover/Thumbnail */}
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0 relative">
        {item.coverUrl ? (
          <img 
            src={item.coverUrl} 
            alt="" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-full ${config?.bgColor} flex items-center justify-center ${item.coverUrl ? 'hidden' : ''}`}>
          <Icon className={`w-6 h-6 ${config?.color}`} />
        </div>
        {item.duration > 0 && (
          <span className="absolute bottom-0.5 right-0.5 bg-background/80 text-white text-[10px] px-1 rounded">
            {formatDuration(item.duration)}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{item.title}</p>
        <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
      </div>

      {/* Type Badge */}
      <Badge variant="outline" className={`text-xs ${config?.color}`}>
        {config?.label}
      </Badge>

      {/* Private Warning */}
      {item.isPrivate && (
        <Badge variant="destructive" className="text-xs">Private</Badge>
      )}

      {/* Selection Indicator */}
      {isSelected && (
        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4 text-primary-foreground" />
        </div>
      )}
    </div>
  );
}

/**
 * Attachment Preview Card - Displays attached content in posts/comments
 */
export function AttachmentPreviewCard({ 
  attachment, 
  onRemove,
  showRequest = true,
  compact = false
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);

  if (!attachment || !attachment.attachmentType) return null;

  const config = ATTACHMENT_TYPES[attachment.attachmentType];
  const Icon = config?.icon || Music;

  const handlePlay = (e) => {
    e.stopPropagation();
    if (!attachment.attachmentPlayUrl) {
      toast.info('Playback not available');
      return;
    }

    if (isPlaying && audioElement) {
      audioElement.pause();
      setIsPlaying(false);
    } else {
      const audio = new Audio(attachment.attachmentPlayUrl);
      audio.play().catch(() => {});
      audio.onended = () => setIsPlaying(false);
      setAudioElement(audio);
      setIsPlaying(true);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-2 p-2 rounded-lg ${config?.bgColor} border border-border/50`}>
        <Icon className={`w-4 h-4 ${config?.color}`} />
        <span className="text-sm truncate flex-1">{attachment.attachmentTitle}</span>
        {onRemove && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove}>
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card 
      className="overflow-hidden bg-muted/30 border-border/50"
      data-testid={`attachment-preview-${attachment.attachmentId}`}
    >
      <div className="flex">
        {/* Cover/Thumbnail */}
        <div className="w-24 h-24 md:w-32 md:h-32 relative flex-shrink-0 bg-muted">
          {attachment.attachmentCoverUrl ? (
            <img 
              src={attachment.attachmentCoverUrl} 
              alt="" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className={`w-full h-full ${config?.bgColor} flex items-center justify-center`}>
              <Icon className={`w-10 h-10 ${config?.color}`} />
            </div>
          )}
          
          {/* Play Button Overlay */}
          {attachment.attachmentPlayUrl && (
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity"
            >
              {isPlaying ? (
                <Pause className="w-10 h-10 text-white" fill="white" />
              ) : (
                <Play className="w-10 h-10 text-white" fill="white" />
              )}
            </button>
          )}

          {/* Duration */}
          {attachment.attachmentDuration > 0 && (
            <span className="absolute bottom-1 right-1 bg-background/80 text-foreground text-xs px-1.5 py-0.5 rounded">
              {formatDuration(attachment.attachmentDuration)}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
          <div>
            <Badge variant="outline" className={`text-xs mb-1 ${config?.color}`}>
              <Icon className="w-3 h-3 mr-1" />
              {config?.label}
            </Badge>
            <h4 className="font-semibold text-sm md:text-base truncate">
              {attachment.attachmentTitle}
            </h4>
            <p className="text-xs text-muted-foreground truncate">
              {attachment.attachmentSubtitle || attachment.attachmentOwnerName}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-2">
            {showRequest && (
              <RequestButton
                targetType={attachment.attachmentType === 'song' ? 'song' : attachment.attachmentType === 'creator' ? 'creator' : 'video'}
                targetId={attachment.attachmentId}
                targetName={attachment.attachmentTitle}
                creatorName={attachment.attachmentOwnerName}
                variant="outline"
                size="sm"
                className="h-7 text-xs"
              />
            )}
            {onRemove && (
              <Button variant="ghost" size="sm" onClick={onRemove} className="h-7">
                <X className="w-4 h-4 mr-1" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default ContentAttachmentPicker;
